/**
 * DUERP-READING.UTILS
 * Lecture d'un document unique existant par l'assistant (generative-assistant) : le prompt, le
 * document a lui joindre, et la conversion de sa reponse en lecture brute.
 * La lecture ignore les parametres du compte : familles, attributs et barèmes sont ceux du document,
 * rapproches ensuite de ceux du compte sans assistant (cf. duerp-mapping.utils).
 * Un Excel se lit d'abord par la structure de ses onglets (cf. duerp-layout.utils) ; cette lecture
 * ligne a ligne sert au PDF, qui part tel quel en piece jointe, et en repli pour un Excel.
 */
import { v4 as uuid } from "uuid"
import { ASSISTANT_DOCUMENT, AssistantCancel, askAssistantJson } from "@/utils/assistant.utils"
import { RawDocument, SCALES, withoutUnusedUnits } from "./duerp-mapping.utils"
import type { SheetGrid } from "./duerp-layout.utils"

//Famille d'un risque que le document ne classe pas : ecartee du rapprochement des familles
const UNDEFINED_FAMILY = "Non défini"
//Lignes d'Excel par appel, et appels en parallele. La duree d'un lot tient surtout a la longueur de sa
//reponse : des petits lots, presque tous lances d'un coup, lisent un Excel courant en moins de 2 minutes.
const BATCH_LINES = 60
const BATCH_CONCURRENCY = 15
//Une ligne de plan d'actions rend une action complete (delais, cout, commentaires) : lots plus petits
const PLAN_BATCH_LINES = 30
//Lignes du debut d'un onglet (titre, en-tetes de colonnes) rappelees dans chacun de ses morceaux
const HEADER_LINES = 5
//Onglets de plan d'actions : ils ne decrivent pas de risque, ils completent ceux des unites (2e passe)
const ACTION_PLAN_SHEET = /plan d.?actions?/i

const INSTRUCTIONS = `This document is a French "Document Unique d'Évaluation des Risques Professionnels" (DUERP). Keep all extracted text in French. Never invent data: omit a field when the document does not state it. Never write a field whose value would be null, "" or [].

WORK UNITS ("unités de travail"): a DUERP groups its risks by work unit. Give each unit the "sheet" where it is described. Each work unit usually appears as its own tab (one sheet per unit), or as a dedicated column or section header in the risk table. List each work unit once. Only create units that are work units of the document; never create one from a word found inside a risk description.

SUMMARY SHEETS: when the workbook has work-unit sheets, its action plans ("plan d'actions"), summaries, risk hierarchies ("hiérarchiser les risques"), accident follow-ups ("suivi AT") and similar recap sheets only repeat or summarize their risks: never create a risk or a unit from such a sheet. Never create a work unit from a code alone (e.g. "U3 B1"): a work unit has a name.

UNITS: each work unit with its name as written in the document, whether it is a job (e.g. "Agents d'entretien") or a place (e.g. "Accueil").
- "map": the plan the unit belongs to, i.e. the larger area that groups several units: a building, a floor, a wing, a geographic site… (e.g. "Bâtiment 1", "Rez-de-chaussée", "1er étage", "Site de Mérignac"). Write it exactly the same way every time for the same plan. Write a numbered building as "Bâtiment <number>" ("B1", "BAT 1", "BÂTIMENT 1", "Unité 0 Bâtiment 1" → "Bâtiment 1"). When the sheet name or the work-unit code identifies the building (e.g. sheet "U7B1" → "Bâtiment 1"), use it. Leave it empty only when nothing identifies the plan.
- Remove the plan from the unit name and put it in "map" (e.g. "Portails et circulations extérieures Bâtiment 1" → name "Portails et circulations extérieures", map "Bâtiment 1").
- A plan alone is not a unit. Exception: when a work unit names only a plan, create a unit with that name and the same name in "map".
- Two units with the same name in different plans are two different units.

RISKS: each line of the risk table is one risk; do not merge lines within a work unit. When a cell (e.g. the risk family) is merged over several lines, apply its value to each line. Never create a risk or a prevention action from an empty row, a heading or a plan label.
- "sources": when the lines of the document start with a reference in brackets (e.g. "[Accueil!12]"), give the references of the line(s) the risk comes from, without the brackets (e.g. "Accueil!12"). Every line that describes a risk must be the source of at least one risk: never skip a risk line, even in a long document. Use [] when the document has no references.
- "family": the risk category exactly as the document writes it (e.g. "Chutes de plain-pied", "Risque routier"), the same way every time. "${UNDEFINED_FAMILY}" when the document gives no category.
- "attributes": the descriptive text columns of the risk table, keyed by their column label exactly as written in the document, the same label every time (e.g. {"Danger": "…", "Situation dangereuse": "…", "Dommages possibles": "…"}). Put the hazard ("phénomène dangereux", "danger") first. Never put scores, work units or prevention measures in attributes.
- "units": the work unit(s) of the risk, each with its "name" and its "map", exactly as in the units list.
- When the same risk (same family, hazard and situation) appears under several units, extract it once and link it to all of them.

SCORES ("severity" = gravité, "frequency" = fréquence / probabilité / exposition, "prevention" = maîtrise / niveau de prévention): copy the numeric value of the document without converting it. If the document only gives a label (e.g. "Hebdomadaire", "Grave"), use its rank in the document's own scale (1 = lowest). Never use the final score (risque brut, risque résiduel, criticité) as a score. Use null when the document gives no score.

SCALES ("scales"): the scoring legend of the document ("grille de cotation", "échelle", "barème"), when this part shows it: for each score, every level with its numeric value and its label (e.g. severity [{"value": 1, "label": "Faible"}, {"value": 4, "label": "Grave"}]). Use the same values as the scores. [] when this part shows no legend.

PREVENTION ACTIONS ("preventionActions") of each risk: one action per measure.
- existing measures ("mesures existantes", "mesures de prévention et moyens de protection existants") → status "done";
- measures in progress → status "pending";
- planned or recommended measures ("actions d'amélioration", "mesures à mettre en place", "plan d'actions") → status "todo".
- "name": a short title of the measure; "description": its full text if longer.
Fill dates (ISO format), cost (a number in euros, without unit) and "financedBy" only if the document gives them, otherwise null.`

//2e passe : chaque ligne du plan d'actions complete le risque deja lu de la meme unite
const ACTION_PLAN_INSTRUCTIONS = `You read lines of the action plan of a French DUERP. Keep all text in French and never invent data. Never write a field whose value would be null, "" or []. Each line gives, for a work unit (often a code such as "U1 B1"), a hazard ("danger identifié"), a prevention action and its follow-up (responsible, deadline, planned cost, completion date, comments). You also get the risks already read from the work-unit sheets, each with its "id", its "sheet" (the work-unit code, e.g. "U1B1") and its "hazard".

For each line that contains an action, return one item. Never skip a line that contains an action, even in a long list. Skip only headers and lines without an action (empty, "0", "#REF!").
- "source": the reference of the line, without brackets (e.g. "Plan d'actions B1!52").
- "riskId": the id of the risk of the SAME work unit with the same hazard. Compare ignoring case, spaces and punctuation; a code matches its sheet whatever its spaces ("U1 B1" = "U1B1"). null when no risk of that work unit matches.
- "unit", "hazard", "family": as written on the line. They are used only when riskId is null; "family" is "${UNDEFINED_FAMILY}" when the line gives none.
- "name": a short title of the action; "description": its full text, followed by the responsible and the comments when given ("Responsable : …", "Commentaire : …").
- "status": "done" when the line says the action is done (e.g. "fait", a completion date), "pending" when it is in progress, otherwise "todo".
- "datePlanned": the deadline when it is a date; "dateDone": the completion date. ISO format, null otherwise. Keep a deadline that is not a date ("immédiat", "1 an", "régulièrement") in the description.
- "cost": the planned cost as a number in euros, without unit; null when it is not a number.
- "financedBy": the funder when given, otherwise null.`

export function getActionPlanPreprompt() {
  const format = {
    actions: [
      {
        source: "",
        riskId: "",
        unit: "",
        hazard: "",
        family: "",
        name: "",
        description: "",
        status: "todo | pending | done",
        datePlanned: null,
        dateDone: null,
        cost: null,
        financedBy: null
      }
    ]
  }
  return `${ACTION_PLAN_INSTRUCTIONS}

Answer only with one JSON object in this format:
${JSON.stringify(format)}`
}

export function getDuerpReadingPreprompt() {
  const format = {
    units: [{ sheet: "", name: "", map: "" }],
    scales: { severity: [{ value: 1, label: "" }], frequency: [], prevention: [] },
    risks: [
      {
        family: "",
        attributes: { "<column label>": "" },
        severity: null,
        frequency: null,
        prevention: null,
        sources: [""],
        units: [{ name: "", map: "" }],
        preventionActions: [
          {
            name: "",
            description: "",
            status: "todo | pending | done",
            dateStarted: null,
            datePlanned: null,
            dateDone: null,
            cost: null,
            financedBy: null
          }
        ]
      }
    ]
  }
  return `${INSTRUCTIONS}

Answer only with one JSON object in this format:
${JSON.stringify(format)}`
}

/**
 * Lecture ligne a ligne, en repli quand la structure des onglets ne donne aucun risque : chaque ligne
 * non vide part en texte, precedee de sa reference (onglet!ligne), que l'assistant cite dans chaque risque.
 * Les onglets partent par lots : en un seul appel, un gros document n'est lu qu'en partie (mesure
 * du 15/10/2026 : 2 onglets d'unite lus sur une soixantaine, sans erreur ni reponse coupee).
 * Les onglets de plan d'actions sont lus a part, apres les unites, pour completer leurs risques.
 */
export function readWorkbook(grids: SheetGrid[]) {
  //Cellules vides en fin de ligne retirees : celles du milieu gardent l'alignement des colonnes
  const sheets = grids.map((grid) => ({
    name: grid.name,
    lines: grid.rows.flatMap((cells, index) => {
      const values = [...cells]
      while (values.length && !values[values.length - 1]) values.pop()
      return values.length ? [`[${grid.name}!${grid.firstRow + index + 1}] ${values.join(" | ")}`] : []
    })
  }))

  //Onglets consecutifs regroupes jusqu'a maxLines lignes ; un onglet plus long est coupe en morceaux
  const toBatches = (batchSheets: typeof sheets, maxLines: number) => {
    const batches: string[][] = []
    let lineCount = 0
    batchSheets.forEach((sheet) => {
      for (let start = 0; start < sheet.lines.length; start += maxLines) {
        const lines = sheet.lines.slice(start, start + maxLines)
        if (!batches.length || lineCount + lines.length > maxLines) {
          batches.push([])
          lineCount = 0
        }
        //Suite d'un onglet coupe : ses premieres lignes (titre, en-tetes de colonnes) rappelees pour le contexte
        const header = start
          ? `(Début de l'onglet, rappelé pour ses en-têtes de colonnes : déjà lu, n'en extrais rien)\n${sheet.lines.slice(0, HEADER_LINES).join("\n")}\n(Suite de l'onglet)\n`
          : ""
        batches[batches.length - 1].push(`### Onglet « ${sheet.name} »\n${header}${lines.join("\n")}`)
        lineCount += lines.length
      }
    })
    return batches.map((batch) => batch.join("\n\n"))
  }

  const sheetNames = sheets.map((sheet) => sheet.name).join(", ")
  return {
    prompts: toBatches(
      sheets.filter((sheet) => !ACTION_PLAN_SHEET.test(sheet.name)),
      BATCH_LINES
    ).map(
      (batch) =>
        `Lis cette partie d'un document unique. Onglets du classeur complet, pour le contexte : ${sheetNames}.\n\n${batch}`
    ),
    actionPlanBatches: toBatches(
      sheets.filter((sheet) => ACTION_PLAN_SHEET.test(sheet.name)),
      PLAN_BATCH_LINES
    )
  }
}

/**
 * Lecture des parties du document, BATCH_CONCURRENCY a la fois. Une partie en echec n'arrete pas les
 * autres : elle rend null.
 */
export function readInBatches(
  parts: { prompt: string; file?: { filename: string; fileData: string } }[],
  preprompt: string,
  onProgress: (done: number) => void
) {
  const cancels: AssistantCancel[] = []
  const results: any[] = []
  let next = 0
  let done = 0
  const read = (part: (typeof parts)[number]) =>
    new Promise<any>((resolve) =>
      cancels.push(
        askAssistantJson({
          profile: ASSISTANT_DOCUMENT,
          preprompt,
          prompt: part.prompt,
          file: part.file,
          onDone: resolve,
          onError: () => resolve(null)
        })
      )
    )
  //Duree et taille de chaque lot, pour regler BATCH_LINES et BATCH_CONCURRENCY sur de vrais documents
  const timedRead = async (part: (typeof parts)[number], index: number) => {
    const start = Date.now()
    const result = await read(part)
    console.info(
      `[duerp] lot ${index + 1}/${parts.length} : ${((Date.now() - start) / 1000).toFixed(1)} s, ` +
        `${part.prompt.length} car. envoyes, ${JSON.stringify(result ?? "").length} car. recus`
    )
    return result
  }
  const worker = async () => {
    while (next < parts.length) {
      const index = next++
      results[index] = await timedRead(parts[index], index)
      onProgress(++done)
    }
  }
  return {
    promise: Promise.all(Array.from({ length: Math.min(BATCH_CONCURRENCY, parts.length) }, worker)).then(() => results),
    cancel: () => cancels.forEach((cancel) => cancel())
  }
}

const key = (value: unknown) =>
  String(value ?? "")
    .trim()
    .toLowerCase()
export const list = (value: unknown) => (Array.isArray(value) ? value : [])
//Onglet d'une reference "onglet!ligne", et code d'unite compare sans espaces ("U1 B1" = "U1B1")
export const sheetOf = (source: unknown) => String(source ?? "").replace(/!\d+$/, "")
const unitCode = (value: unknown) => key(value).replace(/\s+/g, "")

//En base le cout est un entier : "2 000 €" devient 2000, "ACMS" devient null
export function toCost(value: unknown) {
  if (typeof value === "number") return Math.round(value)
  const digits = String(value ?? "")
    .replace(/[^\d,.]/g, "")
    .replace(",", ".")
  return digits ? Math.round(Number(digits)) || null : null
}

function toAction(action: any) {
  return {
    name: String(action?.name ?? "").trim(),
    description: String(action?.description ?? "").trim(),
    status: ["todo", "pending", "done"].includes(action?.status) ? action.status : "todo",
    dateStarted: action?.dateStarted ?? null,
    datePlanned: action?.datePlanned ?? null,
    dateDone: action?.dateDone ?? null,
    cost: toCost(action?.cost),
    financedBy: action?.financedBy ?? null
  }
}

/**
 * Reponses de l'assistant (une par partie du document) -> lecture brute.
 * Une unite appartient a son onglet : deux "Vestiaires" de deux batiments restent deux unites. Les risques
 * s'y rattachent par le nom et le plan, sans tenir compte de la casse ni des espaces, et par l'onglet de
 * leurs lignes sources.
 * Une unite sans risque n'a rien a relire ni a importer.
 */
export function toRawDocument(responses: any[]): RawDocument {
  const all = (field: string) => responses.flatMap((response) => list(response?.[field]))
  const unique = <T>(items: T[], getKey: (item: T) => string) =>
    items.filter((item, index) => items.findIndex((other) => getKey(other) === getKey(item)) === index)

  const units = unique(
    all("units").map((unit: any) => ({
      id: uuid(),
      sheet: String(unit?.sheet ?? ""),
      name: String(unit?.name ?? "").trim(),
      map: unit?.map?.trim() || null
    })),
    (unit) => `${key(unit.sheet)}|${key(unit.name)}|${key(unit.map)}`
  )

  const risks = all("risks").map((risk: any) => {
    const sources = list(risk?.sources).map(String)
    const sourceSheets = sources.map((source) => key(sheetOf(source)))
    //Unite de meme nom dans plusieurs onglets : celle de l'onglet d'ou vient le risque
    const named = units.filter((unit) =>
      list(risk?.units).some(
        (riskUnit: any) => key(riskUnit?.name) === key(unit.name) && key(riskUnit?.map) === key(unit.map)
      )
    )
    const sameSheet = named.filter((unit) => sourceSheets.includes(key(unit.sheet)))
    return {
      sources,
      family: risk?.family || null,
      RiskFamilyId: null,
      attributes: Object.fromEntries(
        Object.entries(risk?.attributes ?? {}).map(([label, value]) => [label.trim(), String(value ?? "").trim()])
      ),
      severity: risk?.severity ?? null,
      frequency: risk?.frequency ?? null,
      prevention: risk?.prevention ?? null,
      preventionActions: list(risk?.preventionActions).map(toAction),
      units: (sameSheet.length ? sameSheet : named).map((unit) => unit.id)
    }
  })

  const used = withoutUnusedUnits({ units, risks })
  return {
    ...used,
    units: used.units.map(({ sheet, ...unit }) => unit),
    //Legende de cotation : la premiere partie qui la donne
    scales: Object.fromEntries(
      SCALES.map((scale) => [
        scale,
        list(responses.find((response) => list(response?.scales?.[scale]).length)?.scales?.[scale])
          .filter((item: any) => typeof item?.value === "number")
          .map((item: any) => ({ value: item.value, label: String(item?.label ?? "").trim() }))
      ])
    )
  }
}

//Risques deja lus, tels que la 2e passe les compare aux lignes du plan d'actions
export function getActionPlanPrompt(document: RawDocument, batch: string) {
  const risks = document.risks.map((risk, index) => ({
    id: `r${index}`,
    sheet: sheetOf(risk.sources?.[0]),
    hazard: Object.values(risk.attributes)[0] ?? ""
  }))
  return `Lignes du plan d'actions :\n\n${batch}\n\nRisques déjà lus dans les onglets des unités :\n${JSON.stringify(risks)}`
}

/**
 * Lignes du plan d'actions -> actions des risques deja lus. Une action deja presente (meme titre ou
 * meme texte) est completee : delais, date de realisation, cout. Une ligne sans risque correspondant
 * cree un risque, rattache a l'unite de son code, avec son danger pour description.
 */
export function addActionPlan(document: RawDocument, responses: any[]): RawDocument {
  const risks = document.risks.map((risk) => ({ ...risk, preventionActions: [...risk.preventionActions] }))
  const hazardLabel = Object.keys(document.risks[0]?.attributes ?? {})[0] ?? "Danger"
  const sameText = (a: string, b: string) =>
    !!a && key(a).replace(/[^\p{L}\d]/gu, "") === key(b).replace(/[^\p{L}\d]/gu, "")

  responses
    .flatMap((response) => list(response?.actions))
    .forEach((item: any) => {
      const action = toAction(item)
      const risk = risks[Number(String(item?.riskId ?? "").replace(/^r/, ""))]
      if (item?.riskId && risk) {
        const existing = risk.preventionActions.find(
          (current) => sameText(current.name, action.name) || sameText(current.description, action.description)
        )
        if (!existing) return risk.preventionActions.push(action)
        Object.assign(existing, {
          status: action.status === "todo" ? existing.status : action.status,
          datePlanned: existing.datePlanned ?? action.datePlanned,
          dateDone: existing.dateDone ?? action.dateDone,
          cost: existing.cost ?? action.cost,
          financedBy: existing.financedBy ?? action.financedBy,
          description:
            action.description.length > existing.description.length ? action.description : existing.description
        })
        return
      }
      //Aucun risque de l'unite ne correspond : nouveau risque, sur l'unite des risques de meme onglet
      const unitRisk = risks.find((current) => unitCode(sheetOf(current.sources?.[0])) === unitCode(item?.unit))
      risks.push({
        sources: [String(item?.source ?? "")],
        family: item?.family || null,
        RiskFamilyId: null,
        //Le danger dans la premiere colonne des risques lus, comme dans les onglets des unites
        attributes: { [hazardLabel]: String(item?.hazard || action.name) },
        //Non cotees par le plan d'actions : la relecture les cale sur le bas de l'echelle du compte
        severity: item?.severity ?? null,
        frequency: item?.frequency ?? null,
        prevention: item?.prevention ?? null,
        preventionActions: [action],
        units: unitRisk?.units ?? []
      })
    })

  return { ...document, risks }
}
